<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useKanbanRefresh } from 'dashboard/composables/useKanbanRefresh';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import kanbanClient from 'dashboard/api/kanban/kanbanClient';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();

const deals = ref([]);
const funnels = ref([]);
const isLoading = ref(true);
const isMoving = ref(false);
const { run, isPending } = useAbortableRequest();

const contactId = computed(() => route.params.contactId);

const closedStages = ['won', 'downsell', 'lost'];

const activeDeals = computed(() =>
  deals.value.filter(
    d => !closedStages.includes(d.kanban_stage?.commercial_kind)
  )
);

const closedDeals = computed(() =>
  deals.value.filter(d =>
    closedStages.includes(d.kanban_stage?.commercial_kind)
  )
);

const formatValue = val => {
  const num = parseFloat(val);
  if (Number.isNaN(num)) return null;
  return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const totalActive = computed(() => {
  let sum = 0;
  activeDeals.value.forEach(d => {
    const v = parseFloat(d.deal_value);
    if (!Number.isNaN(v)) sum += v;
  });
  return sum;
});

const totalWon = computed(() => {
  let sum = 0;
  deals.value
    .filter(d => d.kanban_stage?.commercial_kind === 'won')
    .forEach(d => {
      const v = parseFloat(d.deal_value);
      if (!Number.isNaN(v)) sum += v;
    });
  return sum;
});

const fetchDeals = async (background = false) => {
  if (!contactId.value) return;
  if (background !== true) {
    isLoading.value = true;
    deals.value = [];
  }
  try {
    await run(async signal => {
      const [dealsRes, funnelsRes] = await Promise.all([
        kanbanClient.lookupDealsByContact(contactId.value),
        kanbanClient.getFunnels(),
      ]);
      if (signal.aborted) return;
      deals.value = dealsRes.data;
      funnels.value = funnelsRes.data;
    });
  } catch {
    useAlert(t('KANBAN.ERRORS.FETCH_BOARD'));
  } finally {
    if (!isPending.value) isLoading.value = false;
  }
};

const moveDeal = async (deal, stageId) => {
  if (isMoving.value || deal.kanban_stage?.id === stageId) return;
  const source = `${route.params.accountId}:${contactId.value}`;
  isMoving.value = true;
  try {
    await kanbanClient.moveDeal(
      deal.kanban_funnel?.id,
      deal.id,
      stageId,
      deal.lock_version
    );
    if (source !== `${route.params.accountId}:${contactId.value}`) return;
    useAlert(t('KANBAN.SIDEBAR.MOVED'));
    await fetchDeals(true);
  } catch (error) {
    if (source !== `${route.params.accountId}:${contactId.value}`) return;
    useAlert(
      t(
        error.response?.status === 409
          ? 'KANBAN.CONFLICT.TITLE'
          : 'KANBAN.ERRORS.MOVE_DEAL'
      )
    );
    await fetchDeals(true);
  } finally {
    isMoving.value = false;
  }
};

const stageColor = name => {
  const colors = {
    Ganho:
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    Downsell:
      'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
    Perdido: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  return colors[name] || 'bg-n-alpha-2 text-n-slate-11';
};

watch([contactId, () => route.params.accountId], () => fetchDeals(), {
  immediate: true,
});
useKanbanRefresh(
  () => fetchDeals(true),
  () => !isMoving.value && !isPending.value
);
</script>

<template>
  <div class="px-6 py-4">
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>

    <template v-else>
      <!-- Summary cards -->
      <div v-if="deals.length" class="mb-4 grid grid-cols-2 gap-3">
        <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3">
          <span class="mb-1 block text-xs text-n-slate-10">
            {{ t('KANBAN.REPORTS.ACTIVE_VALUE') }}
          </span>
          <span class="text-sm font-bold text-n-slate-12">
            {{ formatValue(totalActive) || 'R$ 0,00' }}
          </span>
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <span class="ml-1 text-xs text-n-slate-10">
            ({{ activeDeals.length }})
          </span>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3">
          <span class="mb-1 block text-xs text-n-slate-10">
            {{ t('KANBAN.REPORTS.WON_VALUE') }}
          </span>
          <span class="text-sm font-bold text-green-600 dark:text-green-400">
            {{ formatValue(totalWon) || 'R$ 0,00' }}
          </span>
        </div>
      </div>

      <!-- Active deals -->
      <div v-if="activeDeals.length" class="mb-4">
        <h4
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('KANBAN.REPORTS.ACTIVE_DEALS') }}
        </h4>
        <div class="flex flex-col gap-2">
          <div
            v-for="deal in activeDeals"
            :key="deal.id"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
          >
            <div class="mb-2 flex items-center gap-2">
              <span
                v-if="deal.title || deal.product_name"
                class="truncate text-xs font-medium text-n-brand"
              >
                {{ deal.title || deal.product_name }}
                <span
                  v-if="deal.title && deal.product_name"
                  class="block font-normal text-n-slate-11"
                >
                  {{ deal.product_name }}
                </span>
              </span>
              <span v-else class="text-xs text-n-slate-10">
                {{ t('KANBAN.SIDEBAR.TITLE') }}
              </span>
              <span class="flex-1" />
              <span
                v-if="formatValue(deal.deal_value)"
                class="text-xs font-bold text-n-slate-12"
              >
                {{ formatValue(deal.deal_value) }}
              </span>
            </div>

            <!-- Current stage -->
            <div v-if="deal.kanban_stage" class="mb-2">
              <span
                class="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                :style="{ backgroundColor: deal.kanban_stage.color }"
              >
                {{ deal.kanban_stage.name }}
              </span>
            </div>

            <!-- Stage pills for movement -->
            <div
              v-if="deal.kanban_funnel?.kanban_stages"
              class="flex flex-wrap gap-1"
            >
              <button
                v-for="stage in deal.kanban_funnel.kanban_stages"
                :key="stage.id"
                :disabled="isMoving"
                class="rounded-full border px-1.5 py-0.5 text-xs transition-all"
                :class="
                  deal.kanban_stage?.id === stage.id
                    ? 'border-transparent font-semibold text-white'
                    : 'border-n-weak text-n-slate-11 hover:border-n-slate-8 hover:text-n-slate-12'
                "
                :style="
                  deal.kanban_stage?.id === stage.id
                    ? { backgroundColor: stage.color }
                    : {}
                "
                @click="moveDeal(deal, stage.id)"
              >
                {{ stage.name }}
              </button>
            </div>

            <!-- Footer: assignee + date -->
            <div class="mt-2 flex items-center gap-2 text-xs text-n-slate-10">
              <span v-if="deal.assignee?.name" class="truncate">
                {{ deal.assignee.name }}
              </span>
              <span
                v-if="deal.expected_close_date"
                class="flex items-center gap-0.5"
              >
                <span class="i-lucide-calendar size-3" />
                {{
                  new Date(deal.expected_close_date).toLocaleDateString(
                    'pt-BR',
                    { day: '2-digit', month: 'short', timeZone: 'UTC' }
                  )
                }}
              </span>
              <span v-if="deal.lead_source" class="truncate">
                {{ deal.lead_source }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Closed deals -->
      <div v-if="closedDeals.length">
        <h4
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('KANBAN.REPORTS.CLOSED_DEALS') }}
        </h4>
        <div class="flex flex-col gap-2">
          <div
            v-for="deal in closedDeals"
            :key="deal.id"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-3 opacity-75"
          >
            <div class="flex items-center gap-2">
              <span
                v-if="deal.title || deal.product_name"
                class="truncate text-xs font-medium text-n-slate-11"
              >
                {{ deal.title || deal.product_name }}
                <span
                  v-if="deal.title && deal.product_name"
                  class="block font-normal text-n-slate-11"
                >
                  {{ deal.product_name }}
                </span>
              </span>
              <span class="flex-1" />
              <span
                v-if="formatValue(deal.deal_value)"
                class="text-xs font-semibold text-n-slate-11"
              >
                {{ formatValue(deal.deal_value) }}
              </span>
              <span
                class="rounded-full px-1.5 py-0.5 text-xs font-medium"
                :class="stageColor(deal.kanban_stage?.name)"
              >
                {{ deal.kanban_stage?.name }}
              </span>
            </div>
            <div v-if="deal.loss_reason" class="mt-1 text-xs text-n-slate-10">
              {{ deal.loss_reason }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-if="!deals.length"
        class="flex flex-col items-center justify-center py-8 text-n-slate-10"
      >
        <span class="i-lucide-target mb-2 size-8" />
        <p class="text-sm">{{ t('KANBAN.REPORTS.NO_DEALS') }}</p>
      </div>
    </template>
  </div>
</template>
